import { Button, Card, StatusPill } from '@/design-system';
import type { ImpersonationSessionRecord } from '@/entities/tenant';
import { formatDayTimeShort, formatTime } from '@/shared/lib';
import { CardTitleRow } from '@/shared/ui';

import { adminCopy } from '../copy';

export interface ImpersonationSessionsCardProps {
    sessions: readonly ImpersonationSessionRecord[];
    ending?: boolean;
    onEnd: (session: ImpersonationSessionRecord) => void;
}

/**
 * Wie er bij deze organisatie heeft meegekeken, als wie, en waarom. De reden
 * staat er voluit bij: dat is het enige veld dat zo'n regel de moeite waard
 * maakt. Een lopende sessie kun je hier beëindigen; het token vervalt dan meteen.
 */
export function ImpersonationSessionsCard({
    sessions,
    ending = false,
    onEnd,
}: ImpersonationSessionsCardProps) {
    return (
        <Card clip as="section" aria-labelledby="sessions-title">
            <CardTitleRow title={<span id="sessions-title">{adminCopy.sessionsTitle}</span>} />

            {sessions.length === 0 ? (
                <p className="m-0 px-4 py-[13px] text-[13px] leading-[1.5] text-[var(--text-tertiary-safe)]">
                    {adminCopy.sessionsEmpty}
                </p>
            ) : (
                <ul className="m-0 list-none p-0">
                    {sessions.map((session) => (
                        <li
                            key={session.uuid}
                            className="border-b border-[var(--border-subtle-2)] px-4 py-[11px]"
                        >
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span className="min-w-[160px] flex-1 text-[13px] font-medium text-[var(--text-primary)]">
                                    {adminCopy.sessionLine(
                                        session.platformUserName ?? adminCopy.unknown,
                                        session.targetUserName ?? adminCopy.unknown,
                                    )}
                                </span>
                                <StatusPill tone={session.active ? 'warn' : 'neutral'} size="sm">
                                    {session.active
                                        ? adminCopy.sessionActive
                                        : adminCopy.sessionEnded}
                                </StatusPill>
                                <span className="font-[family-name:var(--font-mono)] text-[11px] text-[var(--text-tertiary-safe)]">
                                    {`${formatDayTimeShort(session.startedAt)} · ${adminCopy.sessionUntil(
                                        formatTime(session.endedAt ?? session.expiresAt),
                                    )}`}
                                </span>
                                {session.active ? (
                                    <Button
                                        variant="outline"
                                        size="xs"
                                        disabled={ending}
                                        onClick={() => onEnd(session)}
                                    >
                                        {adminCopy.sessionEnd}
                                    </Button>
                                ) : null}
                            </div>
                            <p className="m-0 mt-1 text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                                {adminCopy.auditReason(session.reason)}
                            </p>
                        </li>
                    ))}
                </ul>
            )}
        </Card>
    );
}
